<template>
  <div class="slds-size--medium">
    <!-- Inserted ranges -->
    <div v-if="filterMode === 'add-remove' && value.length" class="slds-listbox_selection-group selection-box">
      <ul class="slds-listbox slds-listbox_horizontal" role="listbox" aria-label="Selected Options:" aria-orientation="horizontal">
        <li class="slds-listbox-item" :class="{'first-item': index === 0}" role="presentation" v-for="(range, index) in value" :key="index">
          <span class="slds-pill" role="option" :tabindex="index" aria-selected="true">
            <span class="slds-pill__label" :title="range.from + '-' + range.to" v-if="inputType === 'date'">{{range.from | formatDate($t('general.dateFormat'))}} - {{range.to | formatDate($t('general.dateFormat'))}}</span>
            <span class="slds-pill__label" :title="range.from + '-' + range.to" v-else>{{range.from}} - {{range.to}}</span>
            <span class="slds-icon_container slds-pill__remove" title="Remove" @click="removeRange(index)">
              <svg class="slds-icon slds-icon_x-small slds-icon-text-default" aria-hidden="true">
                <use xlink:href="../../../assets/symbols.svg#close"></use>
              </svg>
            </span>
          </span>
        </li>
      </ul>
    </div>

    <div class="slds-size--medium row">
      <div>
        <label class="slds-form-element__label">
          {{ fromLabel }}
        </label>
        <div class="slds-form-element__control" :class="fromValid ? '' : 'slds-has-error'">
          <input
            :disabled="disabled"
            :type="inputType"
            class="slds-input"
            v-model="fromValue"
            @blur="handleBlur"
            @input="clearValidationError"
            @keyup.enter="addRange"
          />
        </div>
      </div>

      <div class="slds-m-horizontal--small">
        <label class="slds-form-element__label">
          {{ toLabel }}
        </label>
        <div class="slds-form-element__control" :class="toValid ? '' : 'slds-has-error'">
          <input
            :disabled="disabled"
            :type="inputType"
            class="slds-input"
            v-model="toValue"
            @blur="handleBlur"
            @input="clearValidationError"
            @keyup.enter="addRange"
          />
        </div>
      </div>

      <div class="slds-form-element__control" v-if="filterMode === 'add-remove'">
        <button class="slds-button slds-button_icon slds-is-selected slds-button_icon-border-filled" :disabled="disabled" :title="$t('general.add')" @click="addRange">
          <svg class="slds-button__icon" aria-hidden="true">
            <use xlink:href="../../../assets/symbols.svg#add"></use>
          </svg>
          <span class="slds-assistive-text">{{ $t('general.add')}}</span>
        </button>
      </div>
    </div>
    <div>
      <div class="slds-form-element__help error" v-if="!fromValid">
        <strong>{{ fromLabel | capitalize }}</strong>: {{ fromError }}
      </div>
      <div class="slds-form-element__help error" v-if="!toValid">
        <strong>{{ toLabel | capitalize }}</strong>: {{ toError }}
      </div>
    </div>
  </div>
</template>
<script>
import moment from 'moment'
// import Validator from '../../../validation/Validator'

const numberComparator = (a, b) => {
  if (a.from < b.from) return -1
  if (a.from === b.from) return 0
  if (a.from > b.from) return 1
}

const dateComparator = (a, b) => {
  if (moment(a.from).isBefore(moment(b.from))) return -1
  if (moment(a.from).isSame(moment(b.from))) return 0
  if (moment(a.from).isAfter(moment(b.from))) return 1
}

export default {
  data () {
    return {
      fromError: '',
      toError: '',
      fromValid: true,
      toValid: true,
      fromValue: '',
      toValue: '',
      localValue: []
    }
  },
  computed: {
    valid () {
      return this.fromValid && this.toValid
    }
  },
  methods: {
    addRange () {
      const { fromValid, toValid } = this.validateLocalRange(true)
      this.fromValid = fromValid
      this.toValid = toValid

      if (fromValid && toValid) {
        if (!this.localValue) {
          this.localValue = []
        }
        if (this.inputType === 'number') {
          this.localValue.push({
            from: parseInt(this.fromValue),
            to: parseInt(this.toValue)
          })
          this.fromValue = ''
          this.toValue = ''

          this.localValue.sort(numberComparator)
        } else if (this.inputType === 'date') {
          this.localValue.push({
            from: this.fromValue,
            to: this.toValue
          })
          this.fromValue = ''
          this.toValue = ''

          this.localValue.sort(dateComparator)
        }

        this.$emit('input', this.localValue)
      }
    },
    clearValidationError () {
      this.fromValid = true
      this.toValid = true
    },
    handleBlur () {
      if (this.filterMode === 'remove-only') {
        const localValue = [{
          from: parseInt(this.fromValue),
          to: parseInt(this.toValue)
        }]
        this.localValue = localValue
        this.$emit('input', localValue)
      }
    },
    removeRange (index) {
      this.localValue.splice(index, 1)
    },
    validateLocalRange (isAdd = false) {
      let from, to
      let fromValid = true
      let toValid = true

      if (isAdd || this.fromValue || this.toValue) {
        switch (this.inputType) {
          case 'number':
            from = parseInt(this.fromValue)
            to = parseInt(this.toValue)

            if (isNaN(from)) {
              this.fromError = this.$t('validation.required')
              fromValid = false
            }

            if (isNaN(to)) {
              this.toError = this.$t('validation.required')
              toValid = false
            }

            if (fromValid && from < 0) {
              this.fromError = this.$t('validation.positiveNumber')
              fromValid = false
            }
            if (toValid && to < 0) {
              this.toError = this.$t('validation.positiveNumber')
              toValid = false
            }

            if (fromValid && from >= to) {
              this.fromError = this.$t('validation.rangeMismatch')
              fromValid = false
            }

            if (fromValid && toValid) {
              // Check if there is the is-in-range validation
              const objectValidations = this.validation.filter(v => typeof v === 'object')
              if (objectValidations[0] && Object.values(objectValidations[0]).includes('is-in-range')) {
                const validationObject = objectValidations.find(v => v.name === 'is-in-range')
                const { param } = validationObject
                if (from < param.from || to > param.to) {
                  this.fromError = this.$t('validation.isInRange')
                  this.toError = this.$t('validation.isInRange')
                  fromValid = false
                  toValid = false
                }
              }
            }
            break
          case 'date':
            from = moment(this.fromValue)
            to = moment(this.toValue)

            if (!from.isValid()) {
              this.fromError = this.$t('validation.required')
              fromValid = false
            }

            if (!to.isValid()) {
              this.toError = this.$t('validation.required')
              toValid = false
            }

            if (fromValid && toValid && from.isAfter(to, 'day')) {
              this.fromError = this.$t('validation.rangeMismatch')
              fromValid = false
            }
            break
        }
      }
      return { fromValid, toValid }
    },
    validate () {
      if (!this.disabled) {
        const { fromValid, toValid } = this.validateLocalRange()
        this.fromValid = fromValid
        this.toValid = toValid
        return fromValid && toValid
      } else {
        return true
      }
    }
  },
  watch: {
    disabled (isDisabled, wasDisabled) {
      if (!wasDisabled && isDisabled) {
        this.fromValue = ''
        this.toValue = ''
      }
    },
    value (newValue) {
      if (this.filterMode === 'remove-only' && newValue.length) {
        this.fromValue = newValue[0].fromValue ? newValue[0].fromValue : newValue[0].from
        this.toValue = newValue[0].toValue ? newValue[0].toValue : newValue[0].to
      } else {
        this.localValue = newValue
      }
    }
  },
  created () {
    if (this.filterMode === 'remove-only' && this.value.length) {
      this.fromValue = this.value[0].fromValue ? this.value[0].fromValue : this.value[0].from
      this.toValue = this.value[0].toValue ? this.value[0].toValue : this.value[0].to
    } else {
      this.localValue = this.value
    }
  },
  props: {
    disabled: Boolean,
    filterMode: String,
    inputType: String,
    fromLabel: String,
    toLabel: String,
    validation: Array,
    value: Array
  }
}
</script>
<style lang="scss" scoped>
  .row {
    display: flex;
    align-items: flex-end;
  }

  .selection-box {
    height: fit-content;
  }

  svg {
    cursor: pointer;
  }

  .error {
    color: #C23933
  }
</style>
